import type {
  Domain,
  Service,
  ContactPartnerQuery,
  AllPagesQuery,
  ContactPartnerQueryVariables,
} from '@/.generated/sanityTypes/graphql'

import type {
  Advisor,
  AllTrustedAdvisorAdIdsQuery,
  AllTrustedAdvisorAdIdsQueryVariables,
  TrustedAdvisorQuery,
  TrustedAdvisorQueryVariables,
} from '@/.generated/tbfTypes/graphql'
import type { GetStaticPropsContext, GetStaticPropsResult } from 'next/types'

import ReactMarkdown from 'react-markdown'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/router'

import Layout from '@/components/layout'
import { MailIcon, LinkedinIcon, AddressCardIcon } from '@/components/icons'
import { BlackButtonLink } from '@/components/buttons'
import Card from '@/components/card'
import { YellowTag } from '@/components/tags'
import { StoryTile } from '@/components/tile'

import { downloadVCard } from '@/lib/vCard'
import { getAllPages } from '@/lib/query'
import { request } from '@/lib/sanityClient'
import { request as requestTbf } from '@/lib/tbfClient'

import { contactPartner as contactPartnerQuery } from './[slug].sanity.graphql'
import {
  trustedAdvisor as trustedAdvisorQuery,
  allTrustedAdvisorAdIds as allTrustedAdvisorAdIdsQuery,
} from './[slug].tbf.graphql'
import { getTypedLocale } from '../../lib/typeHelper'
import Link from 'next/link'
import type { ImageUrlList } from '@/lib/next.tbf-helper'
import { getEmployeesImageURLs } from '@/lib/next.tbf-helper'
import Image from 'next/image'

type ContactDetailsProps = {
  advisor: Advisor
}

const ContactDetails = ({ advisor }: ContactDetailsProps) => {
  const t = useTranslations('advisors')
  const locale = getTypedLocale(useRouter().locale)

  return (
    <div className="flex flex-col gap-1">
      <div className="leading-tight">
        {advisor?.phone && (
          <Link
            href={`tel:${advisor.phone}`}
            className="text-xl text-red-500 hover:text-red-800"
          >
            {advisor.phone}
          </Link>
        )}
      </div>
      <Link
        href={`mailto:${advisor?.email}`}
        className="flex items-center text-red-500 hover:text-red-800"
      >
        <MailIcon className="mr-2 h-5 w-5" />
        {advisor?.email}
      </Link>

      <button
        onClick={() => advisor && downloadVCard(advisor, locale)}
        className="flex items-center text-red-500 hover:text-red-800"
      >
        <AddressCardIcon className="mr-2 h-5 w-5" />
        {t('vcard')}
      </button>

      {advisor?.linkedin && (
        <Link
          target="_blank"
          href={advisor?.linkedin}
          className="flex items-center text-red-500 hover:text-red-800"
        >
          <LinkedinIcon className="mr-2 h-5 w-5" />
          {t('linkedIn')}
        </Link>
      )}
    </div>
  )
}

const ProfilePicture = ({
  picture,
  advisor,
}: {
  picture: string
  advisor: Advisor
}) => {
  return picture ? (
    <div className="relative aspect-square max-w-9 sm:max-w-none">
      <Image
        sizes="(min-width: 1040px) 300px, 28.95vw"
        alt={`${advisor?.firstName ?? ''} ${advisor?.lastName ?? ''}`}
        src={picture}
        fill
        objectFit="cover"
        className="min-h-px w-full"
        priority
      />
    </div>
  ) : null
}

const EmployeeDetails = ({
  advisor,
  services,
  domains,
  profilePictureUrl,
}: {
  advisor: Advisor
  services: Service[]
  domains: Domain[]
  profilePictureUrl: string
}) => {
  const locale = getTypedLocale(useRouter().locale)
  const t = useTranslations('advisors')

  return (
    <Card className="mx-auto max-w-4xl">
      <div className="flex flex-wrap justify-between">
        <div className="sm:w-1/2">
          <div className="text-3xl leading-none">
            <h1 className="font-medium">
              {advisor?.firstName} {advisor?.lastName}
            </h1>
            <p className="text-gray-500">
              {advisor?.roles?.map((role: any) => role[locale]).join(', ') ||
                ''}
            </p>
          </div>
          <div className="flex flex-wrap sm:hidden">
            {advisor && profilePictureUrl && (
              <div className="mt-5">
                <ProfilePicture picture={profilePictureUrl} advisor={advisor} />
                <ContactDetails advisor={advisor} />
              </div>
            )}
          </div>
          <div className="mt-10">
            <h2 className="mb-4 text-red-500">{t('description')}</h2>
            <div className="advisor-description mt-4 text-base leading-tight md:text-xl">
              {advisor?.facts?.[locale] && (
                <ReactMarkdown>{advisor.facts[locale]!}</ReactMarkdown>
              )}
            </div>
          </div>
        </div>
        <div className="hidden w-1/3 sm:block">
          {profilePictureUrl && (
            <ProfilePicture picture={profilePictureUrl} advisor={advisor} />
          )}
          {advisor && (
            <div className="mt-5">
              <ContactDetails advisor={advisor} />
            </div>
          )}
        </div>
      </div>

      <div className="mt-12 space-y-8 text-center sm:mt-20 sm:space-y-12">
        {advisor?.domains && advisor?.domains?.length > 0 && (
          <div>
            <h2 className="mb-4 text-red-500">{t('fieldsOfActivity')}</h2>
            {advisor.domains.map((item) => {
              const domain = domains.find((d) => d.tbfkey === item)
              if (!domain?.title) return null
              return (
                <BlackButtonLink
                  href={'/competences'}
                  key={item}
                  className="mb-3 mr-3 last:mr-0"
                >
                  {domain.title[locale]}
                </BlackButtonLink>
              )
            })}
          </div>
        )}

        {advisor?.services && advisor?.services?.length > 0 && (
          <div>
            <h2 className="mb-4 text-red-500">{t('services')}</h2>
            {advisor?.services?.map((item) => {
              const service = services.find((d) => d.tbfkey === item)
              if (!service?.title) return null
              return (
                <BlackButtonLink
                  href={'/competences'}
                  key={item}
                  className="mb-3 mr-3 last:mr-0"
                >
                  {service.title[locale]}
                </BlackButtonLink>
              )
            })}
          </div>
        )}
      </div>
    </Card>
  )
}

// MAIN COMPONENT

type ContactPartnerPagePtops = {
  data: ContactPartnerQuery & {
    allEmployeesImageURLs: ImageUrlList
  }
  tbfData: TrustedAdvisorQuery
  messages: unknown
  isPreview: boolean
  allPages: AllPagesQuery
}

const ContactPartner = ({
  data,
  tbfData,
  isPreview,
  allPages,
}: ContactPartnerPagePtops) => {
  const router = useRouter()
  const t = useTranslations('advisors')
  const services = data?.allService
  const domains = data?.allDomain
  const advisor = (tbfData?.getTrustedAdvisors as Advisor[])?.[0]
  const advisorFullName = advisor?.firstName + ' ' + advisor?.lastName

  if (router.isFallback) {
    return <div>Loading...</div>
  }

  return (
    <Layout title={advisorFullName} isPreview={isPreview} allPages={allPages}>
      <EmployeeDetails
        services={services as Service[]}
        domains={domains as Domain[]}
        advisor={advisor}
        profilePictureUrl={data?.allEmployeesImageURLs?.[0]?.imageUrl}
      />

      {data?.allAdvisorStories?.[0]?.stories ? (
        <>
          <div className="mb-8 text-center">
            <YellowTag>{t('stories')}</YellowTag>
          </div>

          {(data.allAdvisorStories[0].stories ?? []).map((story) => (
            <Card
              noStyles
              key={story?.slug?.current}
              className="tbf-hover w-full"
              style={{
                height: 'calc(100vh - 65px)',
              }}
            >
              <StoryTile textSize="2/3-1-1" tile={{ link: story }} />
            </Card>
          ))}
        </>
      ) : null}

      <div className="text-center">
        <BlackButtonLink href={`/contact-partners`}>
          {t('back')}
        </BlackButtonLink>
      </div>
    </Layout>
  )
}

export async function getStaticPaths() {
  const pageData = await requestTbf<
    AllTrustedAdvisorAdIdsQuery,
    AllTrustedAdvisorAdIdsQueryVariables
  >(allTrustedAdvisorAdIdsQuery)
  const adIds =
    pageData.data?.trustedAdvisors?.map((ta) => ta?.activeDirectoryId) ?? []

  return {
    paths: adIds.map((adId) => ({ params: { slug: adId } })),
    fallback: true,
  }
}

function getQueryVariables(adId: string) {
  const defaultFilter = { adId: { eq: adId } }
  return {
    whereAdvisor: defaultFilter,
  }
}

export async function getStaticProps(
  context: GetStaticPropsContext,
): Promise<GetStaticPropsResult<ContactPartnerPagePtops>> {
  const slug = context.params?.slug?.toString()
  if (!slug || Array.isArray(context.params)) {
    throw new Error(`slug is not a string, slug is: '${slug}'`)
  }
  const isPreview = context.preview || false
  const queryVariables = getQueryVariables(slug)

  const pageData = await request<
    ContactPartnerQuery,
    ContactPartnerQueryVariables
  >(contactPartnerQuery, queryVariables, isPreview)

  if (pageData.errors)
    throw new Error(`graphql Error occured: ${pageData.errors[0].message}`)

  const tbfData = await requestTbf<
    TrustedAdvisorQuery,
    TrustedAdvisorQueryVariables
  >(trustedAdvisorQuery, { adIds: [slug] })
  if (tbfData.errors)
    throw new Error(
      `graphql Error in tbf API occured: ${tbfData.errors[0].message}`,
    )

  if (!tbfData.data?.getTrustedAdvisors?.length) return { notFound: true }
  // slug is not the same as the alias in some cases
  const alias = tbfData.data.getTrustedAdvisors
    .find((ta) => ta?.activeDirectoryId === slug)
    ?.email?.split('@')[0]
  const allEmployeesImageURLs = await getEmployeesImageURLs([alias])
  return {
    props: {
      data: { ...pageData.data, allEmployeesImageURLs },
      tbfData: tbfData.data,
      messages: (await import(`@/translation/${context.locale}.json`)).default,
      isPreview,
      allPages: await getAllPages(),
    },
    revalidate: 60 * 30, // 30 minutes
  }
}

export default ContactPartner
